import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import UserCard from '../components/UserCard';
import { getInitials, formatHandicap } from '../utils/format';
import Icon from '../components/Icon';
import './Search.css';

/* ─── Connect Modal ───────────────────────────────────────── */
function ConnectModal({ golfer, onClose, onSuccess }) {
  const [metAtCourse, setMetAtCourse] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.sendConnectionRequest(golfer.id, metAtCourse.trim() || undefined, note.trim() || undefined);
      onSuccess(golfer.id);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card connect-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button>

        <div className="connect-modal-golfer">
          <div className="avatar avatar-lg">
            {golfer.photo_url ? <img src={golfer.photo_url} alt={golfer.name} /> : getInitials(golfer.name)}
          </div>
          <div>
            <h3>{golfer.name}</h3>
            {formatHandicap(golfer.handicap) && <p className="connect-modal-meta">{formatHandicap(golfer.handicap)} hdcp</p>}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Where did you meet? <span className="optional">(optional)</span></label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Augusta National, Pebble Beach…"
              value={metAtCourse}
              onChange={e => setMetAtCourse(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Add a note <span className="optional">(optional)</span></label>
            <textarea
              className="form-input form-textarea"
              placeholder="How you know them, a round you played together…"
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={200}
              rows={3}
            />
          </div>
          {error && <p className="form-error">{error}</p>}
          <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
            {submitting ? 'Sending…' : <><Icon name="userPlus" size={18} /> Send connection request</>}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Suggested Golfers Strip ─────────────────────────────── */
function SuggestedStrip({ connectionStatuses, onConnect }) {
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getSuggestedUsers()
      .then(setSuggested)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || suggested.length === 0) return null;


  return (
    <div className="suggested-section">
      <div className="suggested-label">
        <span className="suggested-label-text">People you may know</span>
        <span className="suggested-label-sub">Based on mutual connections</span>
      </div>
      <div className="suggested-scroll">
        {suggested.map(g => {
          const s = connectionStatuses[g.id];
          const isPending = s?.status === 'pending';
          const isConnected = s?.status === 'accepted';
          return (
            <div key={g.id} className="suggested-card">
              <Link to={`/golfer/${g.id}`} className="suggested-card-inner">
                <div className="avatar avatar-md">
                  {g.photo_url ? <img src={g.photo_url} alt={g.name} /> : getInitials(g.name)}
                </div>
                <p className="suggested-name">{g.name.split(' ')[0]}</p>
                {formatHandicap(g.handicap) && <p className="suggested-hdcp">{formatHandicap(g.handicap)} hdcp</p>}
                <p className="suggested-mutual">
                  via {g.sample_mutual_name?.split(' ')[0]}
                  {g.mutual_count > 1 ? ` +${g.mutual_count - 1}` : ''}
                </p>
              </Link>
              {!isPending && !isConnected ? (
                <button
                  className="btn btn-primary btn-xs suggested-connect-btn"
                  onClick={() => onConnect(g)}
                >
                  Connect
                </button>
              ) : (
                <span className="suggested-status">{isConnected ? 'Connected' : 'Pending'}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Golfers Tab ─────────────────────────────────────── */
function GolfersTab() {
  const { user } = useAuth();
  const [golfers, setGolfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [radius, setRadius] = useState(25);
  const [handicapMin, setHandicapMin] = useState('');
  const [handicapMax, setHandicapMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [connectingGolfer, setConnectingGolfer] = useState(null);
  // Monotonic request counter: a slow, stale response must never overwrite
  // the results of a newer one (same guard as CourseAutocomplete's seqRef)
  const seqRef = useRef(0);

  const fetchStatuses = useCallback(async (list) => {
    const statuses = {};
    await Promise.all(list.map(async g => {
      try { statuses[g.id] = await api.getConnectionStatus(g.id); }
      catch { statuses[g.id] = { status: 'none' }; }
    }));
    return statuses;
  }, []);

  const loadNearby = useCallback(async () => {
    if (!user?.latitude || !user?.longitude) { setLoading(false); return; }
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const data = await api.getNearbyUsers(
        user.latitude, user.longitude, radius,
        handicapMin || undefined, handicapMax || undefined
      );
      const statuses = await fetchStatuses(data);
      if (seq !== seqRef.current) return;
      setGolfers(data);
      setConnectionStatuses(statuses);
    } catch (err) { console.error(err); }
    finally { if (seq === seqRef.current) setLoading(false); }
  }, [user, radius, handicapMin, handicapMax, fetchStatuses]);

  const searchGolfers = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) { loadNearby(); return; }
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const data = await api.searchUsers(searchQuery);
      const statuses = await fetchStatuses(data);
      if (seq !== seqRef.current) return;
      setGolfers(data);
      setConnectionStatuses(statuses);
    } catch (err) { console.error(err); }
    finally { if (seq === seqRef.current) setLoading(false); }
  }, [searchQuery, loadNearby, fetchStatuses]);

  useEffect(() => {
    const t = setTimeout(() => searchQuery ? searchGolfers() : loadNearby(), 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchGolfers, loadNearby]);

  const handleConnectSuccess = (userId) => {
    setConnectionStatuses(prev => ({ ...prev, [userId]: { status: 'pending', isInitiator: true } }));
  };

  const getActionButton = (golfer) => {
    const s = connectionStatuses[golfer.id];
    if (!s || s.status === 'none')
      return (
        <button
          className="btn btn-primary btn-block"
          onClick={e => { e.preventDefault(); setConnectingGolfer(golfer); }}
        >
          Connect
        </button>
      );
    if (s.status === 'pending')
      return <button className="btn btn-secondary btn-block" disabled>{s.isInitiator ? 'Request Sent' : 'Pending'}</button>;
    if (s.status === 'accepted')
      return <button className="btn btn-secondary btn-block" disabled><Icon name="check" size={16} /> Connected</button>;
    return null;
  };

  return (
    <div>
      {/* Suggested strip — always shown, handles its own empty state */}
      {!searchQuery && (
        <SuggestedStrip
          connectionStatuses={connectionStatuses}
          onConnect={setConnectingGolfer}
        />
      )}

      <div className="search-controls card">
        <div className="search-bar">
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search by name, course, or city…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <button className="btn btn-secondary filter-toggle" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide' : 'Filters'}
          </button>
        </div>
        {showFilters && (
          <div className="search-filters">
            <div className="filter-group">
              <label className="form-label">Radius</label>
              <select className="form-input form-select" value={radius} onChange={e => setRadius(Number(e.target.value))}>
                <option value={5}>5 mi</option>
                <option value={10}>10 mi</option>
                <option value={25}>25 mi</option>
                <option value={50}>50 mi</option>
                <option value={100}>100 mi</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="form-label">Handicap Range</label>
              <div className="handicap-inputs">
                <input type="number" className="form-input" placeholder="Min" value={handicapMin} onChange={e => setHandicapMin(e.target.value)} min="-10" max="54" />
                <span>–</span>
                <input type="number" className="form-input" placeholder="Max" value={handicapMax} onChange={e => setHandicapMax(e.target.value)} min="-10" max="54" />
              </div>
            </div>
          </div>
        )}
      </div>

      {!user?.latitude && (
        <div className="alert alert-info">
          <p>Add your location to your <Link to="/profile/edit">profile</Link> to discover nearby golfers.</p>
        </div>
      )}

      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : golfers.length > 0 ? (
        <div className="golfers-grid">
          {golfers.map(g => (
            <UserCard
              key={g.id}
              user={g}
              actionButton={getActionButton(g)}
            />
          ))}
        </div>
      ) : searchQuery ? (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="search" size={24} /></div>
          <p className="empty-state-title">No golfers found</p>
          <p className="empty-state-text">No results for "{searchQuery}". Try a different name, course, or city.</p>
        </div>
      ) : !user?.latitude ? (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="pin" size={24} /></div>
          <p className="empty-state-title">Add your location to find nearby golfers</p>
          <p className="empty-state-text">around uses your location to show playing partners in your area. Add it to your profile to get started.</p>
          <Link to="/profile/edit" className="btn btn-primary" style={{ marginTop: 16 }}>Add My Location</Link>
        </div>
      ) : (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="search" size={24} /></div>
          <p className="empty-state-title">No golfers found nearby</p>
          <p className="empty-state-text">No golfers found within {radius} miles. Try expanding your radius or search by name.</p>
        </div>
      )}

      {/* Connect modal */}
      {connectingGolfer && (
        <ConnectModal
          golfer={connectingGolfer}
          onClose={() => setConnectingGolfer(null)}
          onSuccess={handleConnectSuccess}
        />
      )}
    </div>
  );
}

/* ─── Course Card ─────────────────────────────────────── */
function CourseCard({ course }) {
  return (
    <div className="course-card card">
      <div className="course-card-header">
        <div className="course-icon-wrap"><Icon name="flag" size={20} /></div>
        <div className="course-card-info">
          <div className="course-card-name">{course.course_name}</div>
          {(course.city || course.state) && (
            <div className="course-card-loc">
              {[course.city, course.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
        <div className="course-member-count">
          <span className="course-count-num">{course.member_count}</span>
          <span className="course-count-label">{course.member_count === 1 ? 'member' : 'members'}</span>
        </div>
      </div>

      {course.members?.length > 0 && (
        <div className="course-members">
          <div className="course-member-label">Plays here</div>
          <div className="course-member-list">
            {course.members.map(m => (
              <Link key={m.id} to={`/golfer/${m.id}`} className="course-member-chip" title={m.name}>
                <div className="avatar avatar-xs">
                  {m.photo_url ? <img src={m.photo_url} alt={m.name} /> : getInitials(m.name)}
                </div>
                <span className="course-member-name">{m.name.split(' ')[0]}</span>
                {m.handicap != null && (
                  <span className="course-member-hdcp">{formatHandicap(m.handicap)}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Courses Tab ─────────────────────────────────────── */
function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  // Stale-response guard — see GolfersTab
  const seqRef = useRef(0);

  const load = useCallback(async (q = '') => {
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const data = await api.searchCourses(q);
      if (seq !== seqRef.current) return;
      setCourses(data);
    } catch (err) { console.error(err); }
    finally { if (seq === seqRef.current) setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(query), 350);
    return () => clearTimeout(t);
  }, [query, load]);

  return (
    <div>
      <div className="search-controls card">
        <div className="search-bar">
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search by course name, city, or state…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="search-courses-note">
        <Icon name="lock" size={15} /> Golfers control whether their courses appear here — only public ones are shown.
        <Link to="/profile/edit"> Manage yours</Link>
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : courses.length > 0 ? (
        <div className="courses-list">
          {courses.map((c, i) => <CourseCard key={i} course={c} />)}
        </div>
      ) : (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="flag" size={24} /></div>
          <p className="empty-state-title">No courses found</p>
          <p className="empty-state-text">
            {query
              ? 'Try a different course name or location.'
              : <><Link to="/profile/edit">Add your home courses</Link> to show up here.</>}
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Search Page ─────────────────────────────────────── */
function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'golfers';
  const setTab = t => setSearchParams({ tab: t }, { replace: true });

  return (
    <div className="search-page container">
      <div className="search-header">
        <div>
          <h1>{tab === 'courses' ? 'Find Courses' : 'Find Golfers'}</h1>
          <p>{tab === 'courses' ? 'See who plays where' : 'Find playing partners near you'}</p>
        </div>
      </div>

      <div className="search-tabs">
        <button className={`search-tab ${tab === 'golfers' ? 'active' : ''}`} onClick={() => setTab('golfers')}>
          <Icon name="user" size={16} /> Golfers
        </button>
        <button className={`search-tab ${tab === 'courses' ? 'active' : ''}`} onClick={() => setTab('courses')}>
          <Icon name="flag" size={16} /> Courses
        </button>
      </div>

      {tab === 'golfers' ? <GolfersTab /> : <CoursesTab />}
    </div>
  );
}

export default Search;
